import { useCallback, useDeferredValue } from 'react'

import {
	infiniteQueryOptions,
	queryOptions,
	useQuery,
	useQueryClient,
	useSuspenseInfiniteQuery,
	useSuspenseQuery,
} from '@tanstack/react-query'
import { getRouteApi } from '@tanstack/react-router'

import { captureException } from '@/lib/sentry/ui-error-reporter'

import { successStaleTime } from '@/shared/api/utils/query'
import { useCurrentTenant } from '@/shared/auth/store'
import { time } from '@/shared/datetime/utils/time'
import { readLogGeo } from '@/shared/geoip/utils/log-geo'
import { fetchFieldCommonValues } from '@/shared/opensearch/api'
import { dedupeHistogramBuckets } from '@/shared/opensearch/transforms/response'
import { resolveTimeRange } from '@/shared/opensearch/utils/time-preset'

import {
	getLogSchemas,
	getSavedSearches,
	getSingleSavedSearch,
	searchLogs,
} from './api'
import { decodeSavedSearch, prettyfySearchParams } from './utils'

import type {
	SavedSearchPayload,
	SearchIntegrations,
	SearchParams,
	SearchResultHit,
} from './types'
import type {
	HistogramBucket,
	SearchLogResponse,
} from '@/shared/opensearch/types/response'
import type { ResolvedTimeRange } from '@/shared/opensearch/utils/time-preset'

/**
 * 1-indexed page number. Pages after the first carry page 1's window, so a
 * preset `tr` is resolved once per search.
 */
type SearchLogPageParam = { page: number; searchedRange?: ResolvedTimeRange }

/** One results page plus the absolute window it searched. */
type SearchLogPage = SearchLogResponse & {
	searchedRange?: ResolvedTimeRange
}

const Route = getRouteApi('/_dashboard/search/')

const pairLabel = (pair: SearchIntegrations[number]) => `${pair.id}:${pair.ev}`

/** Deduped and sorted by label: one selection in any URL order is one entry. */
const sortedPairs = (pairs: SearchIntegrations): SearchIntegrations =>
	[...new Map(pairs.map((pair) => [pairLabel(pair), pair])).entries()]
		.sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
		.map(([, pair]) => pair)

/**
 * Query keys for search logs
 */
export const searchLogsKeys = {
	all: ['search-logs'] as const,

	search: () => [...searchLogsKeys.all, 'search'] as const,
	searchLogs: (tenant: string, searchParams: SearchParams) =>
		[
			...searchLogsKeys.search(),
			tenant,
			prettyfySearchParams(searchParams),
		] as const,

	logSchemasAll: () => [...searchLogsKeys.all, 'log-schemas'] as const,
	logSchemas: (tenant: string, pairs: SearchIntegrations) =>
		[
			...searchLogsKeys.logSchemasAll(),
			tenant,
			sortedPairs(pairs).map(pairLabel),
		] as const,

	fieldsUsedValues: () =>
		[...searchLogsKeys.all, 'fields-used-values'] as const,

	savedSearches: () => [...searchLogsKeys.all, 'saved-searches'] as const,
	savedSearchList: (tenant: string) =>
		[...searchLogsKeys.savedSearches(), tenant, 'list'] as const,
	savedSearchItem: (tenant: string, name: string) =>
		[...searchLogsKeys.savedSearches(), tenant, name] as const,
}

/**
 * Query options for search logs
 */
export const searchLogsQueries = {
	searchLogs: (tenant: string, searchParams: SearchParams) =>
		infiniteQueryOptions({
			queryKey: searchLogsKeys.searchLogs(tenant, searchParams),
			// Resolve a preset `tr` before `searchLogs` sees it, so the page can
			// carry the window it searched: a label that resolves the preset at
			// render time drifts from cached hits. `searchLogs` resolves again,
			// but an absolute range resolves to itself.
			//
			// Page 1 resolves the window; later pages get it through the page
			// param. Re-resolving per page would search a later window than
			// page 1 and repeat or skip hits across pages.
			queryFn: async ({ pageParam }): Promise<SearchLogPage> => {
				const searchedRange =
					pageParam.searchedRange ?? resolveTimeRange(searchParams.tr)
				const page = await searchLogs(
					{ ...searchParams, tr: searchedRange },
					tenant,
					pageParam.page,
				)

				return { ...page, searchedRange }
			},
			gcTime: time(1, 'm'),
			staleTime: ({ state }) => successStaleTime(state, 1, 'm'),
			initialPageParam: { page: 1 } as SearchLogPageParam,
			getNextPageParam: (
				lastPage,
				_allPages,
				lastPageParam,
			): SearchLogPageParam | undefined => {
				const pageSize = 100

				if (lastPage.hits.hits.length < pageSize) {
					return undefined
				}

				return {
					page: lastPageParam.page + 1,
					searchedRange: lastPage.searchedRange,
				}
			},
			select: (data) => {
				const allHits = data.pages.flatMap((page) =>
					page.hits.hits.map((hit): SearchResultHit => ({
						fields: {
							...(hit._source.event ?? {}),
							'_id': hit._id,
							'@timestamp': hit._source['@timestamp'],
						},
						geo: readLogGeo(hit._source.normalized_fields),
					})),
				)

				const rawAggregationBuckets = data.pages.flatMap<HistogramBucket>(
					(page) => page.aggregations?.events_over_time?.buckets ?? [],
				)

				return {
					hits: allHits,
					// Dedupe + sort merged pages (shared transform — single source).
					aggregations: dedupeHistogramBuckets(rawAggregationBuckets),
					// The first page's window: the one the result count describes.
					searchedRange: data.pages[0]?.searchedRange,
				}
			},
		}),

	/**
	 * The schemas of every selected pair in one `GET logs/schemas`. The
	 * request sends the pairs in the key's order, so the cache entry is
	 * exactly what was asked for.
	 *
	 * `throwOnError: false`: a failed request must cost only the field tree's
	 * sections, not the sidebar. With the global `throwOnError: true` the
	 * error would throw during render; `useFieldSections` turns it into one
	 * failure row per pair instead (GOTCHAS).
	 */
	logSchemas: (tenant: string, pairs: SearchIntegrations) =>
		queryOptions({
			queryKey: searchLogsKeys.logSchemas(tenant, pairs),
			queryFn: ({ signal }) =>
				getLogSchemas(tenant, sortedPairs(pairs), signal),
			gcTime: time(1, 'h'),
			staleTime: ({ state }) => successStaleTime(state, 5, 'h'),
			throwOnError: false,
			enabled: pairs.length > 0,
		}),

	/**
	 * Common-values popover query — POSTs `/logs/search` with an aggs-only
	 * body via `composeCommonValuesBody`, then `select`-transforms the raw
	 * buckets into `{value, count, percent}` rows via `bucketsToCommonValues`.
	 */
	commonValues: (
		tenant: string,
		field: string,
		integrations: SearchIntegrations,
		size: number = 10,
		enabled: boolean = false,
		context?: {
			tr?: SearchParams['tr']
			ft?: SearchParams['ft']
			q?: SearchParams['q']
		},
	) =>
		queryOptions({
			queryKey: [
				...searchLogsKeys.fieldsUsedValues(),
				'common-values',
				tenant,
				field,
				prettyfySearchParams({ int: integrations }),
				size,
				context,
			] as const,
			queryFn: () =>
				fetchFieldCommonValues(tenant, {
					field,
					sources: integrations.map((i): [string, string] => [i.id, i.ev]),
					size,
					tr: resolveTimeRange(context?.tr),
					ft: context?.ft,
					q: context?.q,
				}),
			select: (entries) => ({ entries }),
			gcTime: time(5, 'm'),
			staleTime: ({ state }) => successStaleTime(state, 1, 'm'),
			throwOnError: false,
			enabled: !!field && enabled && integrations.length > 0,
		}),

	savedSearchList: (tenant: string) =>
		queryOptions({
			queryKey: searchLogsKeys.savedSearchList(tenant),
			queryFn: ({ signal }) => getSavedSearches(tenant, signal),
			gcTime: time(1, 'h'),
			staleTime: ({ state }) => successStaleTime(state, 1, 'h'),
		}),

	/**
	 * The one saved search the analyst clicked. Fetched imperatively via
	 * `queryClient.fetchQuery` on click — never rendered as a hook — so the
	 * popover does not send one GET per row just to open. See
	 * `saved-search-button.tsx`.
	 */
	savedSearchItem: (tenant: string, name: string) =>
		queryOptions({
			queryKey: searchLogsKeys.savedSearchItem(tenant, name),
			queryFn: ({ signal }) => getSingleSavedSearch(tenant, name, signal),
			gcTime: time(1, 'h'),
			staleTime: ({ state }) => successStaleTime(state, 1, 'h'),
		}),
}

export const useSearchLogs = () => {
	const tenant = useCurrentTenant()
	const search = Route.useSearch()

	return useSearchLogsQuery(tenant, search)
}

/**
 * `useSearchLogs` for explicit params — the seam its tests render.
 *
 * Every change to integrations, time range or filters is a new query key.
 * Suspending on it would swap the results for skeletons, so the query reads a
 * deferred copy of `search`: React keeps the previous results on screen while
 * the next search loads, and `isStale` is true until it lands. The first load
 * has nothing to keep and still suspends.
 */
export const useSearchLogsQuery = (tenant: string, search: SearchParams) => {
	const deferredSearch = useDeferredValue(search)

	const query = useSuspenseInfiniteQuery(
		searchLogsQueries.searchLogs(tenant, deferredSearch),
	)

	return {
		data: query.data,
		hits: query.data.hits,
		aggregations: query.data.aggregations,
		/** The absolute window the shown results searched. */
		searchedRange: query.data.searchedRange,
		/** False when the user cleared every source: nothing was searched. */
		hasSources: (deferredSearch.int?.length ?? 0) > 0,
		isStale: deferredSearch !== search,
		fetchNextPage: query.fetchNextPage,
		hasNextPage: query.hasNextPage,
		isFetchingNextPage: query.isFetchingNextPage,
		isFetching: query.isFetching,
	}
}

/**
 * Common-values popover hook. Sends full int + tr + ft + q context so the
 * counts reflect what the user is actually looking at (fixes v1 defect).
 */
export const useCommonValues = (
	field: string,
	enabled: boolean = false,
	size: number = 10,
) => {
	const tenant = useCurrentTenant()
	const search = Route.useSearch()

	return useQuery(
		searchLogsQueries.commonValues(
			tenant,
			field,
			search.int ?? [],
			size,
			enabled,
			{ tr: search.tr, ft: search.ft, q: search.q },
		),
	)
}

/**
 * The saved-search list. Suspends; isolated by the `ErrorBoundary` around
 * `SavedSearchesList`, so a failed fetch costs the control, not the page.
 */
export const useSavedSearchList = () => {
	const tenant = useCurrentTenant()

	return useSuspenseQuery(searchLogsQueries.savedSearchList(tenant))
}

/**
 * Fetch and decode one saved search's payload, on demand.
 *
 * Imperative rather than a rendered hook: the list endpoint carries no
 * `search_string`, so a row that fetched on render made opening the popover
 * cost one GET per saved search. Resolves to `null` for legacy v1 rows and
 * malformed JSON; rejects if the fetch does.
 */
export const useLoadSavedSearch = () => {
	const tenant = useCurrentTenant()
	const queryClient = useQueryClient()

	return useCallback(
		async (name: string): Promise<SavedSearchPayload | null> => {
			try {
				const { saved_search } = await queryClient.fetchQuery(
					searchLogsQueries.savedSearchItem(tenant, name),
				)

				return decodeSavedSearch(saved_search.search_string)
			} catch (error) {
				// The caller catches, so no ErrorBoundary sees this. An HTTP failure
				// is an `ApiError`, which `beforeSend` still drops: this is a read the
				// analyst retries by clicking again, and the backend reports its own
				// failures, so it deliberately does not opt in with `reportApiError`
				// (that is for writes nobody else reports). What this does catch is
				// anything else — a bug between the fetch and the decode.
				captureException(error, {
					source: 'react-query',
					feature: 'search',
					action: 'load_saved_search',
					component: 'saved-search-button',
				})
				throw error
			}
		},
		[queryClient, tenant],
	)
}
